/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { GridProps } from '@mui/material';
import { Grid, Box } from '@mui/material';
import React from 'react';
import { breakpoints } from '@opengeoweb/theme';
import type { PublicWarningStatus } from '../../store/warningsForm/types';
import { isEndedStatus } from '../../store/warningsForm/utils';

export const WarningListColumnContent: React.FC<
  GridProps & {
    title?: string;
    status: PublicWarningStatus;
    children?: React.ReactNode;
    width?: number;
  }
> = ({ title, status, children, width = 76, ...props }) => {
  return (
    <Grid
      sx={{
        ...(Array.isArray(props.sx)
          ? Object.assign({}, ...props.sx)
          : props.sx),
        width,
      }}
    >
      {!!title && (
        <Box
          sx={{
            overflow: 'hidden',
            fontWeight: 'normal',
            textOverflow: 'ellipsis',
            fontSize: 10,
            lineHeight: 1.6,
            height: 12,
            color: isEndedStatus(status)
              ? 'geowebColors.typographyAndIcons.inactiveText'
              : 'geowebColors.captions.captionStatus.rgba',
            display: 'block',
            [`@container publicWarningList (min-width: ${breakpoints.desktop}px)`]:
              {
                display: 'none',
              },
          }}
        >
          {title}
        </Box>
      )}
      <Box
        sx={{
          overflow: 'visible',
          fontSize: 12,
          lineHeight: 1.2,
          color: isEndedStatus(status)
            ? 'geowebColors.typographyAndIcons.inactiveText'
            : 'geowebColors.typographyAndIcons.text',
        }}
      >
        {children || '-'}
      </Box>
    </Grid>
  );
};

export default WarningListColumnContent;
